import React, {
  MutableRefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import colorPalette from '../themes/colorPalette';
import Typo from './Typo';
import { ReactNode } from 'react';

const { grey, primary, red, white } = colorPalette;

type InputProps = {
  value: string;
  onChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  size?: INPUT_SIZES;
  round?: boolean;
  zIndex?: number | string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  type?: string;
  label?: string;
  onBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  errorMsg?: string | false;
  innerRef?: MutableRefObject<HTMLInputElement>;
  placeholder?: string;
  [key: string]: any;
};

export enum INPUT_SIZES {
  small = 'small',
  large = 'large',
}

type StyleProps = {
  size: INPUT_SIZES;
  round: boolean;
  label?: string;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  zIndex?: string | number;
  disabled?: boolean;
  errorMsg?: string | false;
};
const useStyles = makeStyles<Theme, StyleProps>({
  input: {
    filter: 'none',
    width: ({ fullWidth }) => (fullWidth ? '100%' : 'auto'),
    padding: ({ startIcon, endIcon, size, label }) => {
      const {
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
      } = getInputPadding(startIcon, endIcon, size, label);
      return `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`;
    },
    borderRadius: ({ round }) => (round ? '28px' : '16px'),
    backgroundColor: ({ errorMsg }) => (errorMsg ? white : grey[2]),
    border: ({ errorMsg }) => `1px solid ${errorMsg ? red[5] : grey[2]}`,
    fontSize: ({ size }) => (size === INPUT_SIZES.small ? '15px' : '17px'),
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: grey[10],
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: '200ms',
    '&:focus': {
      outline: 'none',
      borderColor: ({ errorMsg }) => (errorMsg ? red[5] : primary.main),
      boxShadow: ({ errorMsg }) =>
        errorMsg
          ? '0 0 0 3px rgba(203, 80, 76, 0.5)'
          : '0 0 0 3px rgba(85, 105, 221, 0.4)',
      backgroundColor: white,
      '& + $label': {
        '& > p': {
          transition: '200ms',
          color: ({ errorMsg }) => (errorMsg ? red[5] : primary.main),
        },
      },
    },
    '&:hover': {
      backgroundColor: grey[1],
    },
    '&:disabled': {
      backgroundColor: grey[3],
      color: grey[8],
      '&:hover': {
        backgroundColor: grey[3],
      },
    },
  },
  startIcon: {
    position: 'absolute',
    top: '50%',
    left: '16px',
    display: 'flex',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
  },
  endIcon: {
    position: 'absolute',
    top: '50%',
    right: '16px',
    display: 'flex',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
  },
  container: {
    position: 'relative',
    zIndex: ({ zIndex }) => {
      if (!zIndex) return 1;
      if (typeof zIndex === 'string') return parseInt(zIndex);
      return zIndex;
    },
  },
  label: {
    position: 'absolute',
    top: '50%',
    left: ({ startIcon, endIcon, size, label }) => {
      const { paddingLeft } = getInputPadding(startIcon, endIcon, size, label);
      return `${paddingLeft}px`;
    },
    transform: 'translateY(-50%)',
    cursor: 'auto',
    transition: '200ms',
    '& > p': {
      transition: '200ms',
      color: ({ errorMsg }) => (errorMsg ? red[5] : 'auto'),
    },
  },
  labelTop: {
    top: ({ size }) => (size === INPUT_SIZES.large ? '4px' : '0px'),
    transition: '200ms',
    transform: 'translateY(0)',
    '& > p': {
      fontSize: '12px',
      letterSpacing: '0.07px',
      color: ({ errorMsg }) => (errorMsg ? red[5] : grey[8]),
    },
  },
  errorMsg: {
    color: red[5],
    margin: '4px 0px 0px',
    fontSize: '12px',
    letterSpacing: '0.08px',
    paddingLeft: ({ size }) => (size === INPUT_SIZES.large ? '20px' : '16px'),
    textAlign: 'left',
    fontWeight: 400,
  },
});

function Input({
  startIcon,
  endIcon,
  fullWidth,
  className = '',
  zIndex,
  label,
  type = 'text',
  placeholder,
  size = INPUT_SIZES.large,
  value,
  onChange,
  onBlur,
  disabled,
  errorMsg,
  round = false,
  innerRef,
  ...other
}: InputProps) {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [labelClasses, setLabelClasses] = useState<string[]>(
    placeholder ? ['labelTop'] : []
  );
  const classes = useStyles({
    fullWidth,
    startIcon,
    endIcon,
    zIndex,
    size,
    label,
    disabled,
    errorMsg,
    round,
  });
  const inputRef = useRef<HTMLInputElement | null>(null);

  if (label) {
    other.id = label;
  }

  const labelClassesMapped = labelClasses.map(c => classes[c]).join(' ');

  const onBlurInput = (ev: any) => {
    onBlur && onBlur(ev);
    setTimeout(() => {
      const val = inputRef.current?.value;
      if (val?.length === 0 && !placeholder) {
        setLabelClasses([]);
      }
    });
  };

  useLayoutEffect(() => {
    if (inputRef.current && hasValueInput(inputRef.current)) {
      setLabelClasses(['labelTop']);
    }
  }, []);

  return (
    <>
      <div className={classes.container}>
        {startIcon && (
          <div
            className={classes.startIcon}
            onClick={() => inputRef.current?.focus()}
          >
            {startIcon}
          </div>
        )}
        <input
          className={`${className} ${classes.input}`}
          ref={r => {
            if (innerRef && r) {
              innerRef.current = r;
            }
            inputRef.current = r;
          }}
          value={value ?? inputValue}
          onChange={onChange ?? (ev => setInputValue(ev.target.value))}
          onFocus={() => setLabelClasses(['labelTop'])}
          onBlur={onBlurInput}
          placeholder={placeholder}
          disabled={disabled}
          type={type}
          {...other}
        />
        {label && (
          <label
            className={`${classes.label} ${labelClassesMapped}`}
            htmlFor={label}
          >
            <Typo
              variant={size === INPUT_SIZES.large ? 'bodyMd' : 'bodySm'}
              color="muted"
            >
              {label}
            </Typo>
          </label>
        )}
        {endIcon && (
          <div
            className={classes.endIcon}
            onClick={() => inputRef.current?.focus()}
          >
            {endIcon}
          </div>
        )}
      </div>
      {errorMsg && (
        <p className={classes.errorMsg} aria-live="polite">
          {errorMsg}
        </p>
      )}
    </>
  );
}

function hasValueInput(input: HTMLInputElement) {
  return input && input.value.length > 0;
}

function getInputPadding(
  startIcon: any,
  endIcon: any,
  size: INPUT_SIZES,
  label?: string
) {
  let paddingTop, paddingBottom, paddingLeft, paddingRight;

  if (size === INPUT_SIZES.small && startIcon) {
    paddingLeft = 44;
  } else if (size === INPUT_SIZES.large && startIcon) {
    paddingLeft = 48;
  } else if (size === INPUT_SIZES.small && !startIcon) {
    paddingLeft = 16;
  } else if (size === INPUT_SIZES.large && !startIcon) {
    paddingLeft = 20;
  }

  if (size === INPUT_SIZES.small && endIcon) {
    paddingRight = 44;
  } else if (size === INPUT_SIZES.large && endIcon) {
    paddingRight = 48;
  } else if (size === INPUT_SIZES.small && !endIcon) {
    paddingRight = 16;
  } else if (size === INPUT_SIZES.large && !endIcon) {
    paddingRight = 20;
  }

  if (!label) {
    if (size === INPUT_SIZES.large) {
      paddingTop = 16;
      paddingBottom = 16;
    }
    if (size === INPUT_SIZES.small) {
      paddingTop = 10;
      paddingBottom = 10;
    }
  } else {
    if (size === INPUT_SIZES.large) {
      paddingTop = 24;
      paddingBottom = 8;
    }
    if (size === INPUT_SIZES.small) {
      paddingTop = 16;
      paddingBottom = 4;
    }
  }

  return { paddingTop, paddingRight, paddingBottom, paddingLeft };
}

export default Input;
